import { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { DateTimeInput, Grid, GridColumn, Icon, Input, Select, SelectOption, useIntl } from "@metsooutotec/modes-react-components"
import customerStore from "features/organizations/customers/store/customer-store"
import LowSpinner from "components/common/low-spinner"
import { itemViewDetailsContainerStyles } from "components/common-style/common-style"
import { convertDateToDDMMMYYYY } from "components/utils/date-formatter-utils"
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store"
import { SDASensor } from "features/iot/stock-management/models/sensor-dispatch-allocation"
import { convertValueToOption } from "components/common/select-component-helper"

interface DispatchDetailsSectionProps {
    partNumber: string | undefined
    pickedPartNumber: SelectOption | undefined
    setPickedPartNumber: React.Dispatch<React.SetStateAction<SelectOption | undefined>>
    pickedCustomer: SelectOption | undefined
    setPickedCustomer: React.Dispatch<React.SetStateAction<SelectOption | undefined>>
    pickedSite: SelectOption | undefined
    setPickedSite: React.Dispatch<React.SetStateAction<SelectOption | undefined>>
    listOfCustomers: SelectOption[]
    setListOfSensors: React.Dispatch<React.SetStateAction<SDASensor[]>>
    listOfSites: SelectOption[]
    dispatchDate: Date
    setDispatchDate: React.Dispatch<React.SetStateAction<Date>>
    quantity: number
}

const DispatchDetailsSection: FC<DispatchDetailsSectionProps> = observer(
    ({
        partNumber,
        pickedPartNumber,
        setPickedPartNumber,
        pickedCustomer,
        setPickedCustomer,
        pickedSite,
        setPickedSite,
        listOfCustomers,
        listOfSites,
        setListOfSensors,
        dispatchDate,
        setDispatchDate,
        quantity,
    }) => {
        const { formatMessage } = useIntl()
        const { sensorBatches, allPartNumbers } = sensorBatchStore
        const { customers } = customerStore
        const [listOfPartNumbers, setListOfPartNumbers] = useState<SelectOption[]>([])

        const loadPartNumbers = () => {
            const partNumbers = allPartNumbers
            if (partNumbers.length > 0) {
                setListOfPartNumbers(
                    partNumbers.map(h => ({
                        text: h,
                        value: h,
                    })),
                )
            }
        }
        useEffect(() => {
            loadPartNumbers()
        }, [sensorBatches])

        // Setting Picked Items
        useEffect(() => {
            const partNumberOption = listOfPartNumbers.find(p => p.text.toLowerCase() === partNumber?.toLowerCase())
            if (partNumberOption && partNumberOption.value !== pickedPartNumber?.value) {
                setPickedPartNumber(partNumberOption)
            }
        }, [partNumber, listOfPartNumbers])

        const labels = {
            partNumber: formatMessage("headings.partNumber"),
            customer: formatMessage("headings.customer"),
            site: formatMessage("headings.site"),
            DispatchDate: formatMessage("headings.DispatchDate"),
            quantity: formatMessage("headings.quantity"),
        }

        const onChangeActions = {
            onPartNumber: (pickedValueResult: SelectOption) => {
                setPickedPartNumber(pickedValueResult)
                setListOfSensors([])
            },
            onCustomer: (pickedValueResult: SelectOption) => {
                setPickedCustomer(pickedValueResult)
                setListOfSensors([])
            },
            onSite: (pickedValueResult: SelectOption) => {
                setPickedSite(pickedValueResult)
                setListOfSensors([])
            },
            onDispatchDate: useCallback((value: string) => {
                try {
                    const date = new Date(value)
                    if (isNaN(date.getDate())) {
                        console.warn("Invalid Date!")
                    } else {
                        setDispatchDate(date)
                    }
                } catch (e) {
                    console.warn(e)
                }
            }, []),
        }

        return !customers.length ? (
            <LowSpinner id='customer.loading' />
        ) : (
            <div style={itemViewDetailsContainerStyles}>
                <Grid columns={3}>
                    <GridColumn>
                        {listOfPartNumbers.length > 0 ? (
                            <Select
                                icon={<Icon className='icon-search-16' />}
                                label={labels.partNumber}
                                onChange={value => onChangeActions.onPartNumber(convertValueToOption(value, listOfPartNumbers))}
                                options={listOfPartNumbers}
                                value={pickedPartNumber?.text}
                                required={true}
                            />
                        ) : (
                            <></>
                        )}
                    </GridColumn>
                    <GridColumn>
                        {listOfCustomers.length > 0 ? (
                            <Select
                                icon={<Icon className='icon-search-16' />}
                                label={labels.customer}
                                onChange={value => onChangeActions.onCustomer(convertValueToOption(value, listOfCustomers))}
                                options={listOfCustomers}
                                value={pickedCustomer?.text}
                                required={true}
                            />
                        ) : (
                            <></>
                        )}
                    </GridColumn>
                    <GridColumn>
                        {listOfSites.length > 0 ? (
                            <Select
                                icon={<Icon className='icon-search-16' />}
                                label={labels.site}
                                onChange={value => onChangeActions.onSite(convertValueToOption(value, listOfSites))}
                                options={listOfSites}
                                value={pickedSite?.text}
                                required={true}
                            />
                        ) : (
                            <></>
                        )}
                    </GridColumn>
                    <GridColumn>
                        <DateTimeInput
                            label={labels.DispatchDate}
                            onChange={(value: string | any) => onChangeActions.onDispatchDate(value.toString())}
                            value={convertDateToDDMMMYYYY(dispatchDate)}
                            format='DD-MMM-YYYY'
                            timeFormat={false}
                        />
                    </GridColumn>
                    <GridColumn>
                        <Input label={labels.quantity} value={quantity} readOnly />
                    </GridColumn>
                </Grid>
            </div>
        )
    },
)

export default DispatchDetailsSection
